import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { isObject, get, isEmpty, isString, uniqueId } from "lodash";
import classNames from "classnames";
import {
  crossIcon,
  chevronDownIcon,
  crossRedIcon,
} from "../../../../assets/icons";
import {
  selectSelectedNode,
  selectCollections,
  selectSelectedSecrets,
} from "../../../../pages/DataModels";
import {
  EMPTY_ARRAY,
  EMPTY_STRING,
  KEYDOWN,
  ESCAPE,
  HANDLE_TYPES,
  INDEX,
} from "../../../../constants";
import {
  FIELDS,
  START_NODE_NAME,
  CUSTOM,
} from "../../../../pages/DataModels/datamodels.constants";
import { checkIsConnectionComplete } from "../../../../pages/DataModels/services.helpers";
import {
  getParamProperties,
  getCollectionProperties,
  getValueTypeProperties,
  checkIsInputHandleTypeValue,
  getValidResponseModel,
} from "../../../../pages/DataModels/Expression.helpers";
const { FIRST } = INDEX;
const {
  MODEL_NAME,
  EXPRESSION,
  SELECT_QUERY,
  MODEL_DEFINITION,
  MULTI_COLLECTION,
} = FIELDS;
const { TRIGGER } = HANDLE_TYPES;

const PARAMS = "params";
const COLUMNS = "columns";
const SECRETS = "secrets";
const PARENT_SECTION = "parentSection";
const RECORD_ID = "recordId";
const NOT_FOUND = "notFound";
const FOR_EACH_NODE_ID = 58;
const GET_RECORD_VALUE_NODE_ID = 29;
const REST_API_NODE_ID = 43;
const RUN_JAVASCRIPT_NODE_ID = 42;
const STRIPE_CREATE_CUSTOMER_NODE_ID = 60;
const OUTPUT_MODEL_TYPE = "outputModelType";
const SQL_NODE_ID = 49;

const NotFoundExpression = () => (
  <div className="p-2 ml-4 text-xl h-52 font-bold flex justify-center items-center">
    Not Found!
  </div>
);

const RenderObjectProperties = ({
  properties,
  handleSetExpression,
  paramToBeExpanded,
  handleExpandedParams,
  parentExpression = EMPTY_STRING,
  isOutputHandleTypeObject,
}) =>
  !isEmpty(properties) &&
  properties.map((property) => {
    const isExpanded = paramToBeExpanded.includes(property.name);
    const isExpandable = !isEmpty(property.body);
    const shouldSelectObject =
      isOutputHandleTypeObject &&
      !!property.body.every((field) => isEmpty(field.body));
    if (isOutputHandleTypeObject && isEmpty(property.body)) return null;
    return (
      <div key={uniqueId(property.name)}>
        <div className="flex">
          <img
            src={chevronDownIcon}
            alt="chevron Down"
            className={classNames("duration-300 cursor-pointer", {
              "-rotate-90": !isExpanded,
              invisible: !isExpandable || shouldSelectObject,
            })}
            onClick={() => handleExpandedParams(property.name)}
          />
          <div
            className={classNames("hover:bg-stone-900 px-1 flex-1", {
              "text-sm font-normal": !isExpandable,
            })}
            onClick={() =>
              handleSetExpression(
                parentExpression
                  ? `${parentExpression}.${property.name}`
                  : property.name
              )
            }
          >
            {property.name}
          </div>
        </div>
        <div className="ml-4">
          {isExpanded && (
            <RenderObjectProperties
              properties={property.body}
              handleSetExpression={handleSetExpression}
              paramToBeExpanded={paramToBeExpanded}
              handleExpandedParams={handleExpandedParams}
              parentExpression={
                parentExpression
                  ? `${parentExpression}.${property.name}`
                  : property.name
              }
              isOutputHandleTypeObject={isOutputHandleTypeObject}
            />
          )}
        </div>
      </div>
    );
  });

const RenderExpression = ({
  properties,
  handleSetExpression,
  paramToBeExpanded,
  handleExpandedParams,
  isOutputHandleTypeObject,
}) => {
  if (
    isEmpty(properties) ||
    (isString(properties[FIRST]) && isOutputHandleTypeObject)
  )
    return <NotFoundExpression />;
  else {
    return (
      <div className="p-2 ml-4 text-sm font-bold">
        {!isEmpty(properties) &&
          (!isObject(properties[FIRST]) ? (
            properties.map(
              (property) =>
                isString(property) && (
                  <div
                    key={uniqueId(property)}
                    className="p-1 ml-4 text-sm font-normal hover:bg-stone-900"
                    onClick={() => handleSetExpression(property)}
                  >
                    {property}
                  </div>
                )
            )
          ) : (
            <>
              <RenderObjectProperties
                properties={properties}
                handleSetExpression={handleSetExpression}
                paramToBeExpanded={paramToBeExpanded}
                handleExpandedParams={handleExpandedParams}
                isOutputHandleTypeObject={isOutputHandleTypeObject}
              />
            </>
          ))}
      </div>
    );
  }
};

const NodeCollectionModal = ({
  title,
  nodes,
  edges,
  values,
  subtitle = EMPTY_STRING,
  shouldShow,
  setShouldShow,
  expressionKey,
  expressionSuffix = EMPTY_STRING,
  expressionPrefix = EMPTY_STRING,
  handleChange = () => {},
  handleOnReset = () => {},
  handleOnClose = () => {},
}) => {
  const selectedNode = useSelector(selectSelectedNode);
  const selectedCollection = useSelector(selectCollections);
  const selectedSecrets = useSelector(selectSelectedSecrets);

  const [paramToBeExpanded, setParamToBeExpanded] = useState(EMPTY_ARRAY);
  const [expression, setExpression] = useState();
  const [properties, setProperties] = useState(EMPTY_ARRAY);
  const [expressionFieldSection, setExpressionFieldSection] =
    useState(EMPTY_ARRAY);
  const [selectedExpressionTab, setSelectedExpressionTab] = useState();
  const [selectedSection, setSelectedSection] = useState();
  const [selectedNodeDetails, setSelectedNodeDetails] = useState();
  const [selectedParentNode, setSelectedParentNode] = useState();
  const [collections, setCollections] = useState();
  const valuesRef = useRef(values);

  const isOutputHandleTypeObject =
    selectedNode.nodeId === FOR_EACH_NODE_ID ||
    selectedNode.nodeId === GET_RECORD_VALUE_NODE_ID;

  const handleExpandedParams = (name) => {
    paramToBeExpanded.includes(name)
      ? setParamToBeExpanded(
          paramToBeExpanded.filter((param) => param !== name)
        )
      : setParamToBeExpanded([...paramToBeExpanded, name]);
  };

  useEffect(() => {
    const requiredNode = nodes.find((node) => node.id === selectedNode.id);
    requiredNode && setSelectedNodeDetails(requiredNode.data);
  }, [nodes, selectedNode]);

  useEffect(() => {
    if (selectedCollection)
      setCollections(get(selectedCollection, "data", EMPTY_ARRAY));
  }, [selectedCollection]);

  useEffect(() => {
    const expression = get(values, expressionKey);
    values && shouldShow && setExpression(expression);
  }, [expressionKey, shouldShow, values]);

  useEffect(() => {
    if (!selectedNodeDetails) return;
    const inputLinks = get(selectedNodeDetails, "inputs", EMPTY_ARRAY).reduce(
      (inputLinks, input) => {
        if (input.type !== TRIGGER) inputLinks.push(input.name);
        return inputLinks;
      },
      []
    );

    const shouldShowColumnsSection =
      expressionKey === SELECT_QUERY ||
      expressionKey.includes(EXPRESSION) ||
      selectedNode.nodeId === SQL_NODE_ID;

    const hasNodeModelField =
      MODEL_NAME in valuesRef.current || MULTI_COLLECTION in valuesRef.current;

    const requiredExpressionFieldSection =
      hasNodeModelField && shouldShowColumnsSection
        ? [SECRETS, COLUMNS, PARAMS, ...inputLinks]
        : [SECRETS, PARAMS, ...inputLinks];

    setExpressionFieldSection(requiredExpressionFieldSection);
  }, [selectedNodeDetails, expressionKey, selectedNode]);

  const checkIsConnectedToNotFound = useCallback(() => {
    const currentHandle = selectedNodeDetails.inputs.find(
      ({ name }) => name === selectedExpressionTab
    );
    const currentHandleKey = currentHandle.key;
    const connectedEdge = edges.find(
      ({ target, id }) =>
        target === selectedNodeDetails.id && id.includes(currentHandleKey)
    );
    const connectEdgeSourceHandle = get(
      connectedEdge,
      "sourceHandle",
      EMPTY_STRING
    );
    const isSourceHandleNotFound = connectEdgeSourceHandle.includes(NOT_FOUND);
    if (!isSourceHandleNotFound) return isSourceHandleNotFound;
    const parentNode = nodes.find(
      (node) => node.data.id === connectedEdge.source
    );
    return (
      isSourceHandleNotFound &&
      parentNode &&
      checkIsConnectionComplete(parentNode, nodes, edges)
    );
  }, [selectedNodeDetails, edges, nodes, selectedExpressionTab]);

  useEffect(() => {
    if (!selectedNodeDetails) return;
    if (selectedExpressionTab === COLUMNS) setSelectedSection(COLUMNS);
    else if (selectedExpressionTab === PARAMS) setSelectedSection(PARAMS);
    else if (selectedExpressionTab === SECRETS) setSelectedSection(SECRETS);
    else {
      const inputNode = selectedNodeDetails.inputs.find(
        ({ name }) => name === selectedExpressionTab
      );
      const inputNodeKey = get(inputNode, "key", EMPTY_STRING);
      const selectedNodeConnection = edges.find(
        ({ target, targetHandle }) =>
          target === selectedNodeDetails.id &&
          targetHandle === `tava_${inputNodeKey}`
      );
      if (selectedNodeConnection) {
        const requiredNode = nodes.find(
          (node) => node.id === selectedNodeConnection.source
        );
        setSelectedParentNode(requiredNode);
        const isStartNode =
          get(requiredNode, ["data", "name"]) === START_NODE_NAME;
        const isConnectedToNotFound = checkIsConnectedToNotFound();
        setSelectedSection(
          isStartNode || isConnectedToNotFound ? PARAMS : PARENT_SECTION
        );
      } else setSelectedSection(EMPTY_STRING);
    }
  }, [
    edges,
    nodes,
    selectedExpressionTab,
    selectedNodeDetails,
    checkIsConnectedToNotFound,
  ]);

  useEffect(() => {
    switch (selectedSection) {
      case PARAMS:
        const allParams = getParamProperties(
          nodes,
          collections,
          isOutputHandleTypeObject
        );
        setProperties(allParams);
        break;
      case COLUMNS:
        if (values?.multiCollection) {
          const selectedCollectionsForSQL = values?.multiCollection?.map(
            (each) => each.value
          );
          const properties = getCollectionProperties(
            selectedCollectionsForSQL,
            collections
          );
          setProperties(properties);
        } else {
          const propertiesName =
            values && getCollectionProperties(values.modelName, collections);
          setProperties(propertiesName);
        }
        break;
      case SECRETS:
        const propertiesNames = selectedSecrets.map(({ name }) => name);
        setProperties(propertiesNames);
        break;
      case PARENT_SECTION:
        const isConnectionComplete = checkIsConnectionComplete(
          selectedParentNode,
          nodes,
          edges
        );
        if (!isConnectionComplete) {
          setProperties(EMPTY_ARRAY);
          return;
        }
        if (!checkIsInputHandleTypeValue(selectedParentNode)) {
          if (selectedParentNode.data.nodeId === STRIPE_CREATE_CUSTOMER_NODE_ID) {
            const getModelDefinition = get(
              selectedParentNode,
              ["data", "config"],
              EMPTY_ARRAY
            ).find(({ key }) => key === MODEL_DEFINITION);
            const data = getModelDefinition.defaultValue;
            const validResponseModel = getValidResponseModel(data);
            setProperties(validResponseModel);
          }
          else if (
            selectedParentNode.data.nodeId === REST_API_NODE_ID ||
            selectedParentNode.data.nodeId === RUN_JAVASCRIPT_NODE_ID
          ) {
            const modelType = get(
              selectedParentNode,
              ["data", "config"],
              EMPTY_ARRAY
            ).find(({ key }) => key === OUTPUT_MODEL_TYPE);

            if (modelType.defaultValue === CUSTOM) {
              const getModelDefinition = get(
                selectedParentNode,
                ["data", "config"],
                EMPTY_ARRAY
              ).find(({ key }) => key === MODEL_DEFINITION);
              const data = getModelDefinition.defaultValue;
              const validResponseModel = getValidResponseModel(data);
              setProperties(validResponseModel);
            }
          } else {
            const collectionField = get(
              selectedParentNode,
              ["data", "config"],
              EMPTY_ARRAY
            ).find(({ key, enable }) => key === MODEL_NAME && enable);
            const collectionName = get(
              collectionField,
              "defaultValue",
              EMPTY_STRING
            );
            setProperties(getCollectionProperties(collectionName, collections));
          }
        } else {
          const expressionProperties = getValueTypeProperties(
            selectedParentNode,
            nodes,
            collections
          );
          setProperties(expressionProperties);
        }
        break;
      default:
        setProperties(EMPTY_ARRAY);
    }
  }, [
    edges,
    collections,
    isOutputHandleTypeObject,
    nodes,
    selectedParentNode,
    selectedSection,
    values,
    selectedSecrets,
    selectedNode.nodeId,
  ]);

  const handleSave = () => {
    handleChange(expression.trim(), expressionKey);
    setShouldShow(false);
  };

  const handleSetExpression = useCallback(
    (name) => {
      setExpression(
        `${expression}${expressionPrefix}${selectedExpressionTab}.${name}${expressionSuffix}`
      );
    },
    [expression, expressionPrefix, expressionSuffix, selectedExpressionTab]
  );

  useEffect(() => {
    const close = ({ code }) => code === ESCAPE && setShouldShow(false);
    document.addEventListener(KEYDOWN, close);
    return () => document.removeEventListener(KEYDOWN, close);
  }, [setShouldShow]);

  return (
    <div
      className={classNames(
        "fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[999] overflow-auto transition-opacity duration-300 px-4 flex items-center",
        {
          "inline-block opacity-100": shouldShow,
          "w-0 h-0 opacity-0": !shouldShow,
        }
      )}
    >
      <div className="bg-gray-800 border border-gray-700 rounded w-full mx-auto my-6 sm:w-[600px]">
        <div className="flex items-center p-5 border-b border-gray-700">
          <div>
            <h6
              className={classNames("text-base text-gray-50 font-bold", {
                "mb-2": subtitle,
              })}
            >
              {title}
            </h6>
            <p className="text-sm text-gray-50">{subtitle}</p>
          </div>
          <button
            type="button"
            className="ml-auto rounded-md bg-gray-900"
            onClick={() => {
              setShouldShow(false);
              handleOnReset();
              handleOnClose();
            }}
          >
            <img src={crossIcon} alt="cross" className="w-6" />
          </button>
        </div>
        <div className="border-b p-2 border-gray-200 dark:border-gray-700 text-white">
          <div className="border-b-2 border-gray-900">
            <ul className="flex h-7 text-sm items-end font-medium overflow-auto no-scrollbar">
              {expressionFieldSection.map((section) => (
                <li
                  key={section}
                  className={classNames({
                    hidden: expressionKey === RECORD_ID && section === COLUMNS,
                  })}
                >
                  <button
                    type="button"
                    className={classNames(
                      "inline-block py-1 px-3 text-white hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300 rounded-t-lg",
                      {
                        "bg-gray-900": selectedExpressionTab === section,
                      }
                    )}
                    onClick={() => {
                      if (selectedExpressionTab !== section) {
                        setSelectedExpressionTab(section);
                        setParamToBeExpanded(EMPTY_ARRAY);
                      }
                    }}
                  >
                    {section}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-1 py-2 my-2 h-72 bg-gray-900 overflow-scroll no-scrollbar rounded">
            {selectedExpressionTab && (
              <RenderExpression
                properties={properties}
                handleSetExpression={handleSetExpression}
                handleExpandedParams={handleExpandedParams}
                paramToBeExpanded={paramToBeExpanded}
                isOutputHandleTypeObject={isOutputHandleTypeObject}
              />
            )}
          </div>
          <div className="bg-gray-200 text-black rounded w-full">
            <div className="px-2 font-bold w-full">Expression</div>
            <div className="flex w-full">
              <textarea
                value={expression}
                onChange={(event) => setExpression(event.target.value)}
                className="bg-gray-200 h-24 ml-1 mb-1 text-xs w-[90%] resize-none border-gray-400 border-r-1 border-l-0 border-t-0 border-b-0 focus:ring-transparent"
              />
              <div className="px-2 w-[10%] flex justify-center align-center">
                <button
                  type="button"
                  className="hover:bg-gray-300 rounded"
                  onClick={() => setExpression(EMPTY_STRING)}
                >
                  <img className="w-10 h-10" src={crossRedIcon} alt="cross" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="py-3 px-6 border-t border-gray-700 text-right">
          <button
            className="btn btn-primary-light"
            type="button"
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default NodeCollectionModal;
